import { useState, useEffect } from 'react';
import { categoryService, Category } from '../../services';
import ModalConfirmacao from '../../components/ModalConfirmacao';

export default function Categories() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    order: '0',
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await categoryService.findAll({ page: 1, limit: 100 });
      setCategories(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const data = {
        ...formData,
        order: parseInt(formData.order),
      };

      if (editingCategory) {
        await categoryService.update(editingCategory.id, data);
      } else {
        await categoryService.create(data);
      }

      setShowForm(false);
      setEditingCategory(null);
      setFormData({ name: '', description: '', order: '0' });
      await loadCategories();
    } catch (error) {
      console.error('Erro ao salvar categoria:', error);
      alert('Erro ao salvar categoria');
    }
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      order: String(category.order),
    });
    setShowForm(true);
  };

  // T215: exclusão com o modal do sistema (antes janela nativa) e erro da API
  // na própria tela (antes `alert` do navegador)
  const [categoriaParaExcluir, setCategoriaParaExcluir] = useState<Category | null>(null);
  const [erro, setErro] = useState('');

  const handleDelete = (category: Category) => {
    setErro('');
    setCategoriaParaExcluir(category);
  };

  const confirmarExclusao = async () => {
    const categoria = categoriaParaExcluir;
    if (!categoria) return;
    try {
      await categoryService.delete(categoria.id);
      setCategoriaParaExcluir(null);
      await loadCategories();
    } catch (error: any) {
      console.error('Erro ao excluir categoria:', error);
      setErro(error.response?.data?.message || 'Erro ao excluir categoria');
      setCategoriaParaExcluir(null);
    }
  };

  const handleToggleActive = async (category: Category) => {
    try {
      await categoryService.update(category.id, { is_active: !category.is_active });
      await loadCategories();
    } catch (error) {
      console.error('Erro ao atualizar categoria:', error);
    }
  };

  if (isLoading) {
    return <div className="text-center py-12">Carregando...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-100">🏷️ Categorias</h1>
        <button
          onClick={() => {
            setShowForm(true);
            setEditingCategory(null);
            setFormData({ name: '', description: '', order: '0' });
          }}
          className="btn-primary"
        >
          Nova Categoria
        </button>
      </div>

      {showForm && (
        <div className="card mb-6">
          <h2 className="text-xl font-bold mb-4">
            {editingCategory ? 'Editar Categoria' : 'Nova Categoria'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Nome *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Ordem</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: e.target.value })}
                  className="input-field"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Descrição</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input-field"
                rows={3}
              />
            </div>
            <div className="flex space-x-2">
              <button type="submit" className="btn-primary">
                {editingCategory ? 'Atualizar' : 'Criar'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingCategory(null);
                }}
                className="btn-secondary"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        <div className="overflow-x-auto">
        <table className="w-full min-w-[600px]">
          <thead>
            <tr className="border-b">
              <th className="text-left py-3 px-4">Nome</th>
              <th className="text-left py-3 px-4">Descrição</th>
              <th className="text-left py-3 px-4">Ordem</th>
              <th className="text-left py-3 px-4">Status</th>
              <th className="text-right py-3 px-4">Ações</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((category) => (
              <tr key={category.id} className="border-b hover:bg-gray-700/50">
                <td className="py-3 px-4 font-medium">{category.name}</td>
                <td className="py-3 px-4 text-gray-400">{category.description || '-'}</td>
                <td className="py-3 px-4">{category.order}</td>
                <td className="py-3 px-4">
                  <button
                    onClick={() => handleToggleActive(category)}
                    className={`px-2 py-1 rounded text-xs font-medium ${
                      category.is_active
                        ? 'bg-green-900/60 text-green-300'
                        : 'bg-red-900/60 text-red-300'
                    }`}
                  >
                    {category.is_active ? 'Ativa' : 'Inativa'}
                  </button>
                </td>
                <td className="py-3 px-4 text-right space-x-2">
                  <button
                    onClick={() => handleEdit(category)}
                    className="text-primary-400 hover:text-primary-300"
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => handleDelete(category)}
                    className="text-red-400 hover:text-red-300"
                  >
                    Excluir
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {erro && (
        <p className="text-red-400 text-sm mt-3">{erro}</p>
      )}

      {/* T215: confirmação da exclusão no modal do sistema */}
      {categoriaParaExcluir && (
        <ModalConfirmacao
          titulo="Excluir categoria"
          subtitulo={categoriaParaExcluir.name}
          resumo={[
            'A categoria sai do cardápio do cliente.',
            'Se ainda tiver produtos nela, a exclusão é recusada — mova ou exclua os produtos antes.',
          ]}
          aviso="Não tem volta."
          rotuloConfirmar="Excluir"
          onConfirmar={confirmarExclusao}
          onCancelar={() => setCategoriaParaExcluir(null)}
        />
      )}
    </div>
  );
}
